import * as React from 'react';
import { Form, Button, Container } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Sentry from '@sentry/browser';
import { recaptchaSize } from '../../helpers/common';
import InputFile from '../InputFile';
import {
  submitFormData,
  getBase64,
  validateInputType,
  validateInputSize,
} from '../../helpers/form-api';
import ThankYou, { ThankYouType } from './partials/ThankYou';

const recaptchaKey = process.env.GATSBY_GOOGLE_RECAPTCHA_KEY || '';

export default class NonUrgentMaintenanceForm extends React.Component {
  initialFormData = {
    what_are_you_reporting: '',
    brief_description_of_the_issue: '',
    location: '',
    first_name: '',
    last_name: '',
    email_address: '',
    phone_number: '',
    confirm_cb: false,
  };

  state = {
    error: null,
    success: null,
    submitting: false,
    formData: { ...this.initialFormData },
    errors: {},
  };

  attachmentInput = React.createRef();
  attachmentInputWrapper = React.createRef();
  recaptchaRef = React.createRef();

  handleCaptcha = (value) => {
    this.setState({ captcha: value });
  };

  handleInputChange = (event) => {
    const target = event.currentTarget;
    const value =
      event.target.type === 'checkbox' ? event.target.checked : target.value;
    const name =
      event.target.type === 'checkbox' ? event.target.name : target.name;

    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  handleSelectChange = (event, { name, value }) => {
    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  validateInputs = () => {
    let valid = true;
    this.setState((prevState) => ({
      ...prevState,
      errors: {
        ...prevState.errors,
      },
    }));

    if (this.state.formData.confirm_cb === false) {
      return false;
    }

    let file1;
    if (this.attachmentInput.current) {
      file1 = this.attachmentInput.current.files[0];
      if (!file1) valid = false;
      if (
        file1 &&
        (!validateInputType(file1.type) || !validateInputSize(file1.size, 5))
      ) {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            attachment: 'FileType',
          },
        }));
        valid = false;
      } else {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            attachment: undefined,
          },
        }));
      }
    }
    return valid;
  };

  handleSubmit = async () => {
    try {
      this.setState({
        success: null,
        error: null,
        submitting: true,
      });

      const valid = this.validateInputs();
      if (!valid) {
        if (this.state.formData.confirm_cb === false) {
          this.setState({
            error: 'Please confirm that this report is non-urgent.',
            success: null,
            submitting: false,
          });
        } else if (
          this.attachmentInput.current &&
          this.attachmentInput.current.files[0] === undefined
        ) {
          this.setState({
            error: 'Please attach an image to your report.',
            success: null,
            submitting: false,
          });
        } else {
          this.setState({
            error: 'Missing field.',
            success: null,
            submitting: false,
          });
        }
        return;
      }

      if (this.state.captcha) {
        let file1;
        if (this.attachmentInput.current) {
          file1 = this.attachmentInput.current.files[0];
        }
        let attachment;
        if (file1) {
          attachment = {
            content: await getBase64(file1),
            name: file1.name,
          };
        }

        const formData = {
          ...this.state.formData,
          attachment,
          captchaResponse: this.state.captcha,
        };

        const response = await submitFormData(
          'non_urgent_maintenance',
          formData
        );

        if (response.status !== 200) {
          this.setState({
            success: null,
            error: response.message,
            submitting: false,
            captcha: null,
          });
        } else {
          this.setState({
            success: true,
            emailData: response.emailData,
            error: null,
            submitting: false,
            formData: { ...this.initialFormData },
            captcha: null,
          });

          if (this.attachmentInputWrapper.current) {
            this.attachmentInputWrapper.current.clear();
          }
        }

        if (this.recaptchaRef.current) {
          this.recaptchaRef.current.reset();
        }
      } else {
        this.setState({
          success: null,
          error: 'Please tick ReCAPTCHA',
          submitting: false,
        });
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      this.setState({ error: 'Error', success: null, submitting: false });
    }
  };

  submitText = () => {
    if (this.state.submitting) {
      return 'Submitting...';
    } else if (this.state.success) {
      return 'Submitted';
    }
    return 'Submit';
  };

  render() {
    const whatAreYouReporting = [
      {
        text: 'Graffiti',
        value: 'Graffiti',
        key: 'Graffiti',
      },
      {
        text: 'Non-Urgent Maintenance',
        value: 'Non-Urgent Maintenance',
        key: 'Non-Urgent Maintenance',
      },
      {
        text: 'Pit Lids - Raised, Broken, Sunken',
        value: 'Pit Lids - Raised, Broken, Sunken',
        key: 'Pit Lids - Raised, Broken, Sunken',
      },
      {
        text: 'Ants / Termites',
        value: 'Ants / Termites',
        key: 'Ants / Termites',
      },
      {
        text: 'Bees / Wasps',
        value: 'Bees / Wasps',
        key: 'Bees / Wasps',
      },
    ];

    const {
      what_are_you_reporting,
      brief_description_of_the_issue,
      location,
      first_name,
      last_name,
      email_address,
      phone_number,
      confirm_cb,
    } = this.state.formData;
    const {
      errors: {
        what_are_you_reporting: whatAreYouReportingError,
        attachment: attachmentError,
        confirm_cb: confirmCbError,
      },
    } = this.state;

    if (this.state.success) {
      return (
        <ThankYou
          emailData={this.state.emailData}
          type={ThankYouType.NON_URGENT_MAINTENANCE}
        />
      );
    }

    return (
      <section>
        <Container>
          <div className="grid-wrapper">
            <div className="form-container">
              <Form
                onSubmit={this.handleSubmit}
                data-testid="non-urgent-maintenance-form"
              >
                <h4>Your details</h4>

                <Form.Input
                  label="First Name"
                  required
                  type="text"
                  name="first_name"
                  value={first_name}
                  onChange={this.handleInputChange}
                  id="form-first-name"
                />
                <Form.Input
                  label="Email address"
                  required
                  type="text"
                  name="email_address"
                  value={email_address}
                  onChange={this.handleInputChange}
                  id="form-email-address"
                />

                <Form.Input
                  label="Last Name"
                  required
                  type="text"
                  name="last_name"
                  value={last_name}
                  onChange={this.handleInputChange}
                  id="form-last-name"
                />
                <Form.Input
                  label="Phone number"
                  required
                  type="text"
                  name="phone_number"
                  value={phone_number}
                  onChange={this.handleInputChange}
                  id="form-phone-number"
                />

                <h4>Non Urgent Maintenance Details</h4>

                <div className="select_wrapper">
                  <Form.Select
                    label="What are you reporting?"
                    required
                    options={whatAreYouReporting}
                    placeholder="Please choose"
                    name="what_are_you_reporting"
                    value={what_are_you_reporting}
                    onChange={this.handleSelectChange}
                    id="form-what-are-you-reporting"
                    error={whatAreYouReportingError != null}
                  />
                  <div className="form-field__description">
                    Please{' '}
                    <a
                      href="https://www.powercor.com.au/outages/report-a-streetlight-fault/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      click here
                    </a>{' '}
                    if you are reporting a streetlight fault
                  </div>
                </div>
                <Form.Input
                  label="Brief description of the issue"
                  required
                  name="brief_description_of_the_issue"
                  type="text"
                  value={brief_description_of_the_issue}
                  onChange={this.handleInputChange}
                  id="form-brief-description-of-the-issue"
                />
                <Form.Input
                  label="Location"
                  required
                  type="text"
                  name="location"
                  value={location}
                  onChange={this.handleInputChange}
                  id="form-location"
                />
                <InputFile
                  label="Attach a photo - Required"
                  name="file_1"
                  text="No file chosen"
                  restrictions="Max. filesize 3MB. Please upload only pdf, jpg, tiff, bmp, png, . Note that if form submission fails, please re-upload attachment."
                  id="media_upload"
                  maxFileSize={3}
                  refProp={this.attachmentInput}
                  ref={this.attachmentInputWrapper}
                  error={attachmentError != null}
                  required
                />
                <Form.Checkbox
                  checked={confirm_cb}
                  error={confirmCbError != null}
                  onChange={this.handleInputChange}
                  name="confirm_cb"
                  id="confirm_cb"
                  label="If you are reporting an electricity outage, hazard or emergency please call our 24 hour contact centre. I confirm this report is a non- urgent, non- hazardous issue."
                  required
                />
                <ReCAPTCHA
                  ref={this.recaptchaRef}
                  sitekey={recaptchaKey}
                  onChange={this.handleCaptcha}
                  size={recaptchaSize()}
                />
                <Button
                  className="form--submit-btn"
                  disabled={this.state.submitting}
                  loading={this.state.submitting}
                >
                  {this.submitText()}
                </Button>
                {this.state.error ? (
                  <div className="error-message">{this.state.error}</div>
                ) : null}
              </Form>
            </div>
          </div>
        </Container>
      </section>
    );
  }
}
